// client/src/lib/motion.ts
// Enerlyze v2 — Motion Design System
// All animations reference these tokens. Never hardcode spring/duration values.

import type { Transition, Variants } from "framer-motion"

// ─── Spring Tokens ────────────────────────────────────────────────────────────
// "Data arriving" — precise, no overshoot. For KPI numbers, status badges, data cells.
export const SPRING_DATA: Transition = {
  type: "spring",
  stiffness: 380,
  damping: 40,
  mass: 1,
}

// "System responding" — snappy with a touch of weight. For cards, panels, modals.
export const SPRING_PANEL: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 30,
  mass: 1,
}

// "Energy flowing" — slower, has momentum. For page transitions, large layout shifts.
export const SPRING_FLOW: Transition = {
  type: "spring",
  stiffness: 200,
  damping: 28,
  mass: 1.2,
}

// "Status pulse" — for alert indicators, WAPI color transitions.
export const SPRING_STATUS: Transition = {
  type: "spring",
  stiffness: 500,
  damping: 35,
}

// ─── Tween Tokens ─────────────────────────────────────────────────────────────
// For opacity, color, and visual-only transitions (not layout/position).
export const TWEEN_FAST: Transition = { duration: 0.15, ease: "easeOut" }
export const TWEEN_BASE: Transition = { duration: 0.25, ease: "easeOut" }
export const TWEEN_SLOW: Transition = { duration: 0.4, ease: [0.4, 0, 0.2, 1] }

// ─── Stagger Helpers ──────────────────────────────────────────────────────────
// Use these in parent Variants to cascade child animations.
export const staggerChildren = (delay = 0.06) => ({
  transition: { staggerChildren: delay },
})

export const staggerFast = { transition: { staggerChildren: 0.04 } }
export const staggerSlow = { transition: { staggerChildren: 0.1 } }

// ─── Reusable Variant Sets ────────────────────────────────────────────────────

// Page / route entry — slides up from 12px, fades in.
export const PAGE_VARIANTS: Variants = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { ...SPRING_PANEL, staggerChildren: 0.06 } },
  exit:    { opacity: 0, y: -8, transition: TWEEN_FAST },
}

// Card / panel reveal — used on KPI cards, section panels.
export const CARD_VARIANTS: Variants = {
  hidden:  { opacity: 0, y: 16, scale: 0.98 },
  visible: { opacity: 1, y: 0,  scale: 1, transition: SPRING_PANEL },
}

// Staggered list item — for table rows, inverter fleet items, alert list.
export const LIST_ITEM_VARIANTS: Variants = {
  hidden:  { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0,  transition: SPRING_DATA },
}

// Stat/number reveal — for KPI values, WAPI%, kWh figures.
export const STAT_VARIANTS: Variants = {
  hidden:  { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: SPRING_DATA },
}

// Fade only — for tooltips, overlays, chart legends, secondary info.
export const FADE_VARIANTS: Variants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: TWEEN_BASE },
  exit:    { opacity: 0, transition: TWEEN_FAST },
}

// Sidebar nav item — subtle x-slide on active state.
export const NAV_ITEM_VARIANTS: Variants = {
  inactive: { opacity: 0.6, x: 0 },
  active:   { opacity: 1,   x: 2, transition: SPRING_STATUS },
}

// WAPI banner — scale + colour is driven by data, but the enter animation uses this.
export const WAPI_VARIANTS: Variants = {
  hidden:  { opacity: 0, scaleX: 0.97 },
  visible: { opacity: 1, scaleX: 1, transition: SPRING_FLOW },
}

// Tab underline / indicator — for the site tab nav active indicator.
export const TAB_INDICATOR_VARIANTS: Variants = {
  hidden:  { opacity: 0, scaleX: 0 },
  visible: { opacity: 1, scaleX: 1, transition: SPRING_STATUS },
}

// Chart section reveal — triggered by whileInView.
export const CHART_REVEAL_VARIANTS: Variants = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0,  transition: { ...SPRING_FLOW, delay: 0.1 } },
}

// ─── Loop Tokens ──────────────────────────────────────────────────────────────
// Continuous ambient animations — energy flowing through the system.

// Sidebar glow line — slow upward scroll, fiber-optic feel.
export const LOOP_SLOW: Transition = { duration: 8, repeat: Infinity, ease: "linear" }

// WAPI banner pulse — alive, monitoring in real time.
export const LOOP_PULSE: Transition = { duration: 3, repeat: Infinity, ease: "easeInOut" }

// Table scanning line — system reading data rows.
export const LOOP_SCAN: Transition = { duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 6 }

// Live status dot — heartbeat indicator.
export const LOOP_DOT: Transition = { duration: 2, repeat: Infinity, ease: "easeInOut" }

// ─── Drag Tokens ────────────────────────────────────────────────────────────
// "Snap release" — snappy spring for drag end. Power Flow widget drag-and-drop.
export const SPRING_DRAG: Transition = { type: "spring", stiffness: 600, damping: 35, mass: 0.8 }
// "Settle" — for nodes returning to default positions on reset.
export const SPRING_SNAP: Transition = { type: "spring", stiffness: 400, damping: 30 }

// ─── Energy Colour ────────────────────────────────────────────────────────────
// Primary "energy" colour for all ambient light effects.
export const ENERGY_COLOR = "#38bdf8" // sky-400
